import { camelize } from "camelscore";
import GrailedAPIError, {
  fromResponse as grailedErrorFromResponse,
  fromReason as grailedErrorFromReason,
} from "../Error";
import type { Options } from "./Options";
import { fromOptions as headersFromOptions } from "./Headers";
import { fromOptions as timeoutFromOptions } from "./Timeout";
import RequestWatcher from "./RequestWatcher";

const goatenvEnvoyEgress = process.env.GOATENV_ENVOY_EGRESS;

const baseUrl = goatenvEnvoyEgress || "";

function parseText<T>(text: string): T {
  // We sometimes return 200's with empty bodies and set it to JSON, which isn't valid (should be
  // 204 / not JSON). In the meantime, we skip over empty bodies here
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!text) return {} as T;
  return camelize(JSON.parse(text));
}

function parseResponse<T>(rsp: Response): Promise<T> {
  return rsp.text().then((text: string) => parseText<T>(text));
}

const throwError = (error: GrailedAPIError): never => {
  throw error;
};

export const requestWatcher = new RequestWatcher();

const request = <T>(
  url: string,
  opts: Options | null | undefined,
): Promise<T> => {
  requestWatcher.addRequest(url, opts?.body);

  const { body, method } = opts || {};
  const headers = new Headers(headersFromOptions(opts));
  if (goatenvEnvoyEgress) headers.set("x-goat-app", "grailed-rails");
  // Setting credentials to "include" in development will allow cookies to be included in
  // cross-origin requests so we can login from storefront locally
  // See More: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
  const credentials =
    process.env.NODE_ENV === "development" ? "include" : "same-origin";
  const [timeoutId, signal] = timeoutFromOptions(opts);
  const options: RequestInit = {
    body,
    method,
    headers,
    credentials,
    signal,
  };

  const handleResponse = (response: Response): Promise<T> | Promise<never> => {
    clearTimeout(timeoutId);
    if (response.ok)
      return parseResponse<T>(response).catch((reason) => {
        return throwError(grailedErrorFromReason(response, options)(reason));
      });
    return grailedErrorFromResponse(options)(response).then(throwError);
  };

  return fetch(baseUrl + url, options)
    .then(handleResponse)
    .finally(() => clearTimeout(timeoutId));
};

export default request;
